import type { Student } from "@/lib/student.schema";
import { SUBJECT_LABELS, YEAR_LABELS, parsePerYearScores } from "./score-labels";

interface MathPhysicsScoresProps {
	student: Student;
}

interface ScoreRow {
	label: string;
	/** Điểm theo lớp 10 / 11 / 12, `null` ở năm nguồn không có. */
	byYear: (string | null)[];
}

/**
 * Mã ngành được hiện khối điểm Toán/Lý theo quy chế tuyển sinh 2026.
 *
 * QUAN TRỌNG — đừng "sửa" bằng cách thêm `7440102_NN`: đó là nhóm ngành của
 * mùa 2025 (Vật lý học + Công nghệ Vật lý điện tử và tin học + Công nghệ bán
 * dẫn gộp chung, 278 sinh viên cũ vẫn còn mã này trong DB). Từ 2026, Công nghệ
 * bán dẫn tách thành mã ngành riêng `74401a1` và `7440102_NN` chỉ còn Vật lý
 * học + Công nghệ Vật lý điện tử và tin học — KHÔNG thuộc diện hiện khối này.
 */
export const MATH_PHYSICS_ELIGIBLE_MAJOR_CODES: readonly string[] = [
	"74401a1", // Công nghệ bán dẫn (CNBD)
	"75202a1", // Thiết kế vi mạch (TKVM)
	"7520402", // Kỹ thuật hạt nhân (KTHN)
];

/**
 * Điểm Toán / Vật lý — đọc thẳng từ hai cột `Toan_HB` / `VatLy_HB` của file
 * upload, không còn nội suy từ điểm học bạ 3 năm (`Diem_Lop10/11/12`).
 *
 * Nguồn gộp cả ba năm vào MỘT ô văn bản
 * ("Toán L10: 8.90; Toán L11: 9.40; Toán L2: 9.50"), nên khối này tách ra theo
 * lớp thay vì in nguyên chuỗi: in nguyên chuỗi làm ô cao ba dòng, không so
 * được theo năm, và không xếp cột với bảng "Điểm 3 năm THPT" ngay bên trên dù
 * hai bảng nói về cùng ba năm đó.
 *
 * Chỉ hiện với sinh viên trúng tuyển vào một trong ba ngành ở
 * `MATH_PHYSICS_ELIGIBLE_MAJOR_CODES` (xem comment ở đó) — các ngành khác
 * không render khối này.
 */
export default function MathPhysicsScores({ student }: MathPhysicsScoresProps) {
	if (!MATH_PHYSICS_ELIGIBLE_MAJOR_CODES.includes(student.ma_nganh)) {
		return null;
	}

	const rows: ScoreRow[] = [
		{ label: SUBJECT_LABELS.math, byYear: parsePerYearScores(student.toan_hb) },
		{ label: SUBJECT_LABELS.physics, byYear: parsePerYearScores(student.vat_ly_hb) },
	];

	// Không có điểm nào đọc được thì nói thẳng, thay vì một bảng toàn dấu "-" —
	// bảng rỗng trông như lỗi hiển thị. Khối vẫn được render để cột điểm giữ
	// nguyên chiều cao (xem `hoc-ba.scores.tsx`).
	const hasAnyScore = rows.some((row) => row.byYear.some((score) => score !== null));

	return (
		<div className="rounded-lg border-2 border-sky-300 bg-sky-50 p-3 space-y-2">
			<div className="text-base font-bold text-sky-900">Điểm Toán - Vật lý</div>

			{!hasAnyScore ? (
				<div className="text-sm font-medium text-sky-700">Chưa có điểm</div>
			) : (
				<table className="w-full table-fixed">
					<thead>
						<tr className="border-b border-sky-300">
							<th className="py-1 text-left text-sm font-semibold text-sky-800">Môn</th>
							{YEAR_LABELS.map((label) => (
								<th key={label} className="py-1 text-center text-sm font-semibold text-sky-800">
									{label}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => (
							<tr key={row.label} className="border-b border-sky-200 last:border-b-0">
								<td className="py-1 text-left text-sm font-semibold text-sky-900">{row.label}</td>
								{row.byYear.map((score, index) => (
									<td
										key={YEAR_LABELS[index]}
										className="py-1 text-center text-lg font-bold text-sky-700"
									>
										{score ?? "-"}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			)}
		</div>
	);
}
